/**
 * OrgEntitlementsPanel
 *
 * SYSTEM_ADMIN panel for granting and revoking org entitlements.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, RefreshCw, Loader2, ShieldCheck } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { useJurisdictions } from '@/hooks/useJurisdictions';
import type { OrgEntitlement, Organization } from '@/types/database';

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchEntitlements(orgId?: string): Promise<OrgEntitlement[]> {
  const params = new URLSearchParams();
  if (orgId) params.set('org_id', orgId);
  const res = await apiFetch(`/api/entitlements?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load entitlements: ${res.status}`);
  const json = await res.json();
  return (json.data ?? []) as OrgEntitlement[];
}

async function fetchOrgs(): Promise<Organization[]> {
  const res = await apiFetch('/api/organizations');
  if (!res.ok) throw new Error(`Failed to load organizations: ${res.status}`);
  const json = await res.json();
  return (json.data ?? []) as Organization[];
}

// ── Grant dialog ──────────────────────────────────────────────────────────────

interface GrantFormData {
  org_id: string;
  jurisdiction_id: string;
  status: 'active' | 'lapsed';
  valid_from: string;
  valid_until: string;
}

const EMPTY_GRANT: GrantFormData = {
  org_id: '',
  jurisdiction_id: '',
  status: 'active',
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: '',
};

interface GrantDialogProps {
  orgs: Organization[];
  onSave: (data: GrantFormData) => Promise<void>;
}

function GrantDialog({ orgs, onSave }: GrantDialogProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<GrantFormData>(EMPTY_GRANT);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { flat: jurisdictions } = useJurisdictions();

  function handleOpen(next: boolean) {
    if (next) {
      setForm(EMPTY_GRANT);
      setErr(null);
    }
    setOpen(next);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.org_id || !form.jurisdiction_id) {
      setErr('Organization and jurisdiction are required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(form);
      setOpen(false);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : 'Failed to grant entitlement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Grant Entitlement
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Grant Org Entitlement</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Organization *</Label>
            <Select
              value={form.org_id}
              onValueChange={(v) => setForm({ ...form, org_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization…" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Jurisdiction *</Label>
            <Select
              value={form.jurisdiction_id}
              onValueChange={(v) => setForm({ ...form, jurisdiction_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select jurisdiction…" />
              </SelectTrigger>
              <SelectContent>
                {jurisdictions.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Granting a node also grants access to all its descendants.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) =>
                  setForm({ ...form, status: v as 'active' | 'lapsed' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lapsed">Lapsed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Valid from</Label>
              <Input
                type="date"
                value={form.valid_from}
                onChange={(e) =>
                  setForm({ ...form, valid_from: e.target.value })
                }
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>
              Valid until{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </Label>
            <Input
              type="date"
              value={form.valid_until}
              onChange={(e) =>
                setForm({ ...form, valid_until: e.target.value })
              }
            />
            <p className="text-xs text-gray-500">
              Leave blank for no expiry. Past dates trigger lapse freeze.
            </p>
          </div>

          {err && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {err}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Grant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline revoke confirm ─────────────────────────────────────────────────────

function RevokeButton({ onConfirm }: { onConfirm: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <Button
          variant="destructive"
          size="sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
              setConfirming(false);
            }
          }}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Revoke'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
          ✕
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="text-red-400 hover:text-red-600"
      title="Revoke"
      onClick={() => setConfirming(true)}
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export const OrgEntitlementsPanel: React.FC = () => {
  const [entitlements, setEntitlements] = useState<OrgEntitlement[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgFilter, setOrgFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ents, orgList] = await Promise.all([
        fetchEntitlements(orgFilter || undefined),
        fetchOrgs(),
      ]);
      setEntitlements(ents);
      setOrgs(orgList);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [orgFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleGrant = async (data: GrantFormData) => {
    const res = await apiFetch('/api/entitlements', {
      method: 'POST',
      body: JSON.stringify({
        org_id: data.org_id,
        jurisdiction_id: data.jurisdiction_id,
        status: data.status,
        valid_from: data.valid_from || new Date().toISOString(),
        valid_until: data.valid_until || null,
      }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(
        (e as { error?: string }).error ?? 'Failed to grant entitlement'
      );
    }
    await load();
  };

  const handleRevoke = async (id: string) => {
    await apiFetch(`/api/entitlements/${id}`, { method: 'DELETE' });
    setEntitlements((prev) => prev.filter((e) => e.id !== id));
  };

  const handleSetStatus = async (id: string, status: 'active' | 'lapsed') => {
    await apiFetch(`/api/entitlements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    setEntitlements((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status } : e))
    );
  };

  const orgName = (orgId: string) =>
    orgs.find((o) => o.id === orgId)?.name ?? orgId;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Org Entitlements
            </CardTitle>
            <CardDescription>
              Grant organizations access to jurisdiction data. Active grants
              unlock full read/write; lapsed grants freeze to overlay-only.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void load()}
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
            <GrantDialog orgs={orgs} onSave={handleGrant} />
          </div>
        </div>

        {/* Org filter */}
        <div className="mt-3 flex items-center gap-2">
          <Label className="shrink-0 text-sm text-gray-600">
            Filter by org:
          </Label>
          <Select
            value={orgFilter || '__all__'}
            onValueChange={(v) => setOrgFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All organizations</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {error && (
          <div className="border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading entitlements…
          </div>
        ) : entitlements.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No entitlements found. Grant one above.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valid from</TableHead>
                <TableHead>Valid until</TableHead>
                <TableHead className="w-32" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entitlements.map((e) => {
                type WithJur = OrgEntitlement & {
                  jurisdictions?: { name: string; level: string };
                };
                const jur = (e as WithJur).jurisdictions;
                const isLapsed =
                  e.status !== 'active' ||
                  (!!e.valid_until && new Date(e.valid_until) < new Date());

                return (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {orgName(e.org_id)}
                    </TableCell>
                    <TableCell>
                      {jur ? (
                        <span>
                          <span className="font-medium">{jur.name}</span>
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {jur.level}
                          </Badge>
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-gray-400">
                          {e.jurisdiction_id}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={isLapsed ? 'destructive' : 'default'}
                        className="cursor-pointer"
                        onClick={() =>
                          void handleSetStatus(
                            e.id,
                            isLapsed ? 'active' : 'lapsed'
                          )
                        }
                        title="Click to toggle"
                      >
                        {isLapsed ? 'Lapsed' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {e.valid_from
                        ? new Date(e.valid_from).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {e.valid_until
                        ? new Date(e.valid_until).toLocaleDateString()
                        : '∞'}
                    </TableCell>
                    <TableCell>
                      <RevokeButton onConfirm={() => handleRevoke(e.id)} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
